import type { Ref } from "vue";
import { ApolloQueryResult, FetchResult } from "@apollo/client/core";
import {
  ChainInput,
  DssConnectionSettingInput,
  GetChainCountOverCustomerQuery,
  GetChainCountOverCustomerDocument,
  CreateDssConnectionSettingMutation,
  CreateDssConnectionSettingDocument,
  CreateChainMutation,
  CreateChainDocument,
  OperationInput,
} from "@/generated/graphql";
import { getWordList } from "@/common/graphql/expression";
import { getChainList } from "@/common/graphql/chain";
import { generateApolloClient } from "@/common/apolloClientUtil";

/** チェーン登録 API通信に必要な依存をまとめた型。 */
interface UseChainRegisterApiDeps {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
}

/**
 * チェーン登録 API通信を扱う composable。
 * GraphQL通信の一元管理、エラーハンドリングを行う。
 */
export const useChainRegisterApi = (deps: UseChainRegisterApiDeps) => {
  const { errdialog, errMessage } = deps;

  /**
   * 文言データを取得する。
   */
  async function getWordListData(
    chainCode: string,
    screenId: string,
    customerCode: string
  ): Promise<Record<string, string>> {
    return await getWordList(chainCode, screenId, customerCode);
  }

  /**
   * チェーンリストを取得する（名前重複チェック用）。
   */
  async function getChainListData(
    customerCode: string
  ) {
    return await getChainList(customerCode);
  }

  /**
   * チェーンコード重複チェック（顧客横断）。
   */
  async function getChainCountOverCustomerData(
    chainCode: string,
    customerCode: string
  ): Promise<number> {
    const apolloClient = await generateApolloClient();
    const result: ApolloQueryResult<GetChainCountOverCustomerQuery> = await apolloClient.query({
      query: GetChainCountOverCustomerDocument,
      variables: {
        customer_code: customerCode,
        chain_code: chainCode,
      },
      fetchPolicy: "no-cache",
    });
    return result.data.getChainCountOverCustomer!.count;
  }

  /**
   * DSS接続設定を作成する。
   */
  async function createDssConnectionSettingData(
    input: DssConnectionSettingInput,
    logInput: OperationInput
  ): Promise<CreateDssConnectionSettingMutation | null | undefined> {
    const apolloClient = await generateApolloClient();
    const result: FetchResult<CreateDssConnectionSettingMutation> =
      await apolloClient.mutate({
        mutation: CreateDssConnectionSettingDocument,
        variables: { input, logInput },
      });
    return result.data;
  }

  /**
   * チェーンを作成する。
   */
  async function createChainData(
    input: ChainInput
  ): Promise<CreateChainMutation | null | undefined> {
    const apolloClient = await generateApolloClient();
    const result: FetchResult<CreateChainMutation> =
      await apolloClient.mutate({
        mutation: CreateChainDocument,
        variables: { input },
      });
    return result.data;
  }

  return {
    getWordListData,
    getChainListData,
    getChainCountOverCustomerData,
    createDssConnectionSettingData,
    createChainData,
  };
};
